class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') {
        this.close();
      }
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();

    // Fix quantity inputs immediately
    this.fixQuantityInputs();

    // Watch Shopify AJAX re-renders
    this.startQuantityObserver();
  }

  /* =========================================================
     HEADER CART ICON
  ========================================================= */

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (
        event.code.toUpperCase() === 'SPACE' ||
        event.code.toUpperCase() === 'ENTER'
      ) {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /* =========================================================
     OPEN CART DRAWER
  ========================================================= */

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote =
      this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    // IMPORTANT
    // Fix quantity fields every time drawer opens
    this.fixQuantityInputs();

    setTimeout(() => {
      this.fixQuantityInputs();

      this.classList.add('animate', 'active');
    }, 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement
          );
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    if (typeof giftCart === 'function') {
      giftCart();
    }
  }

  /* =========================================================
     CLOSE CART DRAWER
  ========================================================= */

  close() {
    this.classList.remove('active');

    if (
      typeof removeTrapFocus === 'function'
    ) {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove(
      'overflow-hidden'
    );

    if (typeof giftCart === 'function') {
      giftCart();
    }
  }

  /* =========================================================
     SUMMARY ACCESSIBILITY
  ========================================================= */

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute(
      'role',
      'button'
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false'
    );

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest('details');

        if (!details) return;

        event.currentTarget.setAttribute(
          'aria-expanded',
          !details.hasAttribute('open')
        );
      }
    );

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  /* =========================================================
     FIX ALL INVALID QUANTITY INPUTS
  ========================================================= */

  fixQuantityInputs() {
    const inputs = document.querySelectorAll(
      'input[type="number"].quantity__input, ' +
      'input.quantity__input, ' +
      'input[name^="updates"], ' +
      'input[name*="quantity"]'
    );

    inputs.forEach((input) => {
      this.fixSingleQuantityInput(input);
    });
  }

  /* =========================================================
     FIX ONE QUANTITY INPUT
  ========================================================= */

  fixSingleQuantityInput(input) {
    if (!input) return;

    let minValue = parseInt(
      input.getAttribute('min'),
      10
    );

    /*
     * If min is invalid, use 0.
     */
    if (isNaN(minValue)) {
      minValue = 0;
    }

    /*
     * Read max.
     */
    const maxAttribute =
      input.getAttribute('max');

    let maxValue = null;

    if (
      maxAttribute !== null &&
      maxAttribute !== ''
    ) {
      maxValue = parseInt(
        maxAttribute,
        10
      );
    }

    /*
     * ======================================================
     * IMPORTANT FIX
     *
     * If Shopify generates:
     *
     * min="0"
     * max="-78"
     *
     * remove max completely.
     *
     * This is the error shown in your screenshot.
     * ======================================================
     */

    if (
      maxValue !== null &&
      (
        isNaN(maxValue) ||
        maxValue < minValue
      )
    ) {
      input.removeAttribute('max');

      /*
       * Some custom cart code may use data-max.
       */
      input.removeAttribute('data-max');

      maxValue = null;
    }

    /*
     * Fix current quantity.
     */
    let currentValue = parseInt(
      input.value,
      10
    );

    if (
      isNaN(currentValue) ||
      currentValue < minValue
    ) {
      currentValue = minValue;
    }

    /*
     * Apply max only when max is valid.
     */
    if (
      maxValue !== null &&
      !isNaN(maxValue) &&
      maxValue >= minValue &&
      currentValue > maxValue
    ) {
      currentValue = maxValue;
    }

    input.value = currentValue;

    /*
     * Remove browser validation error.
     */
    input.setCustomValidity('');

    /*
     * Prevent duplicate event listeners.
     */
    if (
      input.dataset.quantityFixAttached === 'true'
    ) {
      return;
    }

    input.dataset.quantityFixAttached = 'true';

    /*
     * ======================================================
     * QUANTITY INPUT EVENT
     * ======================================================
     */

    input.addEventListener(
      'input',
      () => {
        this.validateQuantityInput(input);
      }
    );

    input.addEventListener(
      'change',
      () => {
        this.validateQuantityInput(input);
      }
    );
  }

  /* =========================================================
     VALIDATE QUANTITY
  ========================================================= */

  validateQuantityInput(input) {
    if (!input) return;

    let minValue = parseInt(
      input.getAttribute('min'),
      10
    );

    if (isNaN(minValue)) {
      minValue = 0;
    }

    /*
     * Read max again because Shopify/custom scripts
     * may change it dynamically.
     */

    let maxValue = null;

    const maxAttribute =
      input.getAttribute('max');

    if (
      maxAttribute !== null &&
      maxAttribute !== ''
    ) {
      maxValue = parseInt(
        maxAttribute,
        10
      );
    }

    /*
     * If max is invalid, remove it.
     */

    if (
      maxValue !== null &&
      (
        isNaN(maxValue) ||
        maxValue < minValue
      )
    ) {
      input.removeAttribute('max');
      input.removeAttribute('data-max');

      maxValue = null;
    }

    /*
     * Read current value.
     */

    let value = parseInt(
      input.value,
      10
    );

    if (isNaN(value)) {
      value = minValue;
    }

    /*
     * Minimum.
     */

    if (value < minValue) {
      value = minValue;
    }

    /*
     * Maximum only if valid.
     */

    if (
      maxValue !== null &&
      !isNaN(maxValue) &&
      maxValue >= minValue &&
      value > maxValue
    ) {
      value = maxValue;
    }

    input.value = value;

    /*
     * Clear native validation.
     */

    input.setCustomValidity('');
  }

  /* =========================================================
     OBSERVE SHOPIFY AJAX CART UPDATES
  ========================================================= */

  startQuantityObserver() {
    if (this.quantityObserver) {
      return;
    }

    this.quantityObserver =
      new MutationObserver(() => {

        /*
         * Small delay allows Shopify to finish
         * replacing the cart HTML.
         */

        clearTimeout(
          this.quantityFixTimer
        );

        this.quantityFixTimer =
          setTimeout(() => {
            this.fixQuantityInputs();

            if (
              typeof giftCart === 'function'
            ) {
              giftCart();
            }
          }, 20);
      });

    this.quantityObserver.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );
  }

  /* =========================================================
     RENDER CART CONTENT
  ========================================================= */

  renderContents(parsedState) {
    const drawerInner =
      this.querySelector('.drawer__inner');

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach(
      (section) => {

        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) return;

        const sectionHTML =
          parsedState.sections &&
          parsedState.sections[
            section.id
          ];

        if (!sectionHTML) return;

        try {
          sectionElement.innerHTML =
            this.getSectionInnerHTML(
              sectionHTML,
              section.selector
            );
        } catch (error) {
          console.error(
            'Cart drawer render error:',
            error
          );
        }

        /*
         * Shopify has now replaced the HTML.
         * Fix inputs again.
         */

        this.fixQuantityInputs();

        if (
          typeof giftCart === 'function'
        ) {
          giftCart();
        }
      }
    );

    setTimeout(() => {

      const overlay =
        this.querySelector(
          '#CartDrawer-Overlay'
        );

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this)
        );
      }

      /*
       * Final quantity validation.
       */

      this.fixQuantityInputs();

      this.open();

    }, 50);
  }

  /* =========================================================
     GET SECTION HTML
  ========================================================= */

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return '';

    const parsedHTML =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedHTML.querySelector(
        selector
      );

    if (!element) {
      console.warn(
        'Cart drawer selector not found:',
        selector
      );

      return '';
    }

    return element.innerHTML;
  }

  /* =========================================================
     SECTIONS TO RENDER
  ========================================================= */

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  /* =========================================================
     SECTION DOM
  ========================================================= */

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) return null;

    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(selector);
  }

  /* =========================================================
     ACTIVE ELEMENT
  ========================================================= */

  setActiveElement(element) {
    this.activeElement = element;
  }
}


/* =========================================================
   DEFINE CART DRAWER
========================================================= */

if (
  !customElements.get('cart-drawer')
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}


/* =========================================================
   CART DRAWER ITEMS
========================================================= */

if (
  typeof CartItems !== 'undefined' &&
  !customElements.get('cart-drawer-items')
) {

  class CartDrawerItems extends CartItems {

    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

  }

  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}


/* =========================================================
   GIFT CART
========================================================= */

function giftCart() {

  const cartItems =
    document.querySelectorAll(
      'tr.cart-item'
    );

  cartItems.forEach(
    (cartItem) => {

      const dataId =
        cartItem.getAttribute(
          'data-id'
        );

      if (!dataId) return;

      const drawerFooter =
        document.querySelector(
          `#Gift-Wrapper[data-id="${dataId}"]`
        );

      if (!drawerFooter) return;

      const drawerFooterID =
        drawerFooter.getAttribute(
          'data-id'
        );

      if (
        drawerFooterID === dataId
      ) {

        drawerFooter.classList.add(
          'Gift-Wrapper__hide'
        );

      }

    }
  );
}


/* =========================================================
   GLOBAL QUANTITY FIX
========================================================= */

function globalQuantityFix() {

  const inputs =
    document.querySelectorAll(
      'input[type="number"], ' +
      'input[name^="updates"], ' +
      'input[name*="quantity"]'
    );

  inputs.forEach(
    (input) => {

      let min =
        parseInt(
          input.getAttribute('min'),
          10
        );

      if (isNaN(min)) {
        min = 0;
      }

      const maxAttribute =
        input.getAttribute('max');

      if (
        maxAttribute !== null
      ) {

        const max =
          parseInt(
            maxAttribute,
            10
          );

        /*
         * MAIN FIX
         */

        if (
          isNaN(max) ||
          max < min
        ) {

          input.removeAttribute(
            'max'
          );

          input.removeAttribute(
            'data-max'
          );
        }
      }

      /*
       * Clear native validation.
       */

      input.setCustomValidity('');
    }
  );
}


/* =========================================================
   FIX BEFORE CHECKOUT
========================================================= */

document.addEventListener(
  'click',
  (event) => {

    const checkoutButton =
      event.target.closest(
        '[name="checkout"], ' +
        'button[type="submit"][name="checkout"], ' +
        'a[href*="/checkout"]'
      );

    if (!checkoutButton) return;

    /*
     * IMPORTANT:
     * Fix invalid number inputs BEFORE
     * Shopify/browser validation runs.
     */

    globalQuantityFix();

  },
  true
);


/* =========================================================
   FIX BEFORE FORM SUBMIT
========================================================= */

document.addEventListener(
  'submit',
  (event) => {

    globalQuantityFix();

    /*
     * Check all number inputs in this form.
     */

    const form =
      event.target;

    if (!form) return;

    const inputs =
      form.querySelectorAll(
        'input[type="number"]'
      );

    inputs.forEach(
      (input) => {

        let min =
          parseInt(
            input.getAttribute('min'),
            10
          );

        if (isNaN(min)) {
          min = 0;
        }

        const maxAttribute =
          input.getAttribute('max');

        if (
          maxAttribute !== null
        ) {

          const max =
            parseInt(
              maxAttribute,
              10
            );

          if (
            isNaN(max) ||
            max < min
          ) {

            input.removeAttribute(
              'max'
            );

            input.removeAttribute(
              'data-max'
            );
          }
        }

        input.setCustomValidity('');
      }
    );

  },
  true
);


/* =========================================================
   DOM READY
========================================================= */

document.addEventListener(
  'DOMContentLoaded',
  () => {

    globalQuantityFix();

    const cartDrawer =
      document.querySelector(
        'cart-drawer'
      );

    if (cartDrawer) {
      cartDrawer.fixQuantityInputs();
    }

    if (
      typeof giftCart === 'function'
    ) {
      giftCart();
    }

  }
);


/* =========================================================
   EXTRA SAFETY CHECK
========================================================= */

setTimeout(() => {

  globalQuantityFix();

}, 500);